import { useCallback, useMemo, useEffect } from 'react';
import { useForm, useFieldArray, UseFormReturn } from 'react-hook-form';
import {
  mapFormFieldsToTimesheetLabels,
  mapTimesheetLabelsToFormFields,
  mergeResetLabels
} from 'modules/notes/common/utils';
import { TimesheetLabel } from 'modules/timesheet-labels/labelSchema';
import {
  NOTE_FIELDS,
  NoteFormData,
  TimesheetLabelFields
} from 'modules/notes/common/noteSchema';

export interface UseMultiEditFormResultProps {
  formMethods: UseFormReturn;
  isValidForm: boolean;
  isFormDirty: boolean;
  timesheetLabelFields: TimesheetLabelFields;
}

export const getDefaultValues = ({
  labels
}: {
  labels: TimesheetLabel[];
}): NoteFormData => {
  return {
    timesheetLabels: mapTimesheetLabelsToFormFields(false, labels)
  };
};

export const useMultiEditForm = (
  labels: TimesheetLabel[]
): UseMultiEditFormResultProps => {
  const defaultValues = useMemo(
    () =>
      getDefaultValues({
        labels
      }),
    [labels]
  );

  const formMethods = useForm({
    defaultValues,
    shouldUnregister: false,
    mode: 'onChange'
  });
  const { formState, control, handleSubmit, getValues, reset } = formMethods;

  const { isDirty, isValid, dirtyFields } = formState;

  const handleSubmitWithTransform = useCallback(
    (onValid, onInvalid) => {
      return handleSubmit(async (data: NoteFormData) => {
        const transformedLabelFields = mapFormFieldsToTimesheetLabels(
          data?.timesheetLabels
        );
        return onValid({
          ...data,
          timesheetLabels: transformedLabelFields
        });
      }, onInvalid);
    },
    [handleSubmit]
  );

  useEffect(() => {
    // if timesheet labels popuplated/changed after initial values (e.g. date changed)
    const values = getValues();
    const resetLabels = mergeResetLabels(defaultValues, values);
    if (resetLabels) {
      reset(
        { ...values, [NOTE_FIELDS.TIMESHEET_LABELS]: resetLabels },
        {
          keepErrors: true, // errors will not be reset
          keepDirty: true // dirtyFields will not be reset
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues.timesheetLabels]);

  const { fields } = useFieldArray({
    control,
    name: NOTE_FIELDS.TIMESHEET_LABELS,
    keyName: 'key'
  });

  const isFormDirty = useMemo(() => {
    return isDirty && !!Object.keys(dirtyFields).length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Object.keys(dirtyFields).length, isDirty]);

  const isValidForm = useMemo(() => isFormDirty && isValid, [
    isFormDirty,
    isValid
  ]);

  return {
    formMethods: useMemo(
      () => ({
        ...formMethods,
        handleSubmit: handleSubmitWithTransform
      }),
      [formMethods, handleSubmitWithTransform]
    ),
    isValidForm,
    isFormDirty,
    timesheetLabelFields: (fields as unknown) as Record<'key', TimesheetLabel>[]
  };
};
